import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Building, Shield, LogIn, Loader2 } from 'lucide-react';
import { formatApiDetail } from '../utils/errorFormatter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OrgLoginPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await fetch(`${API_URL}/api/company/public/slug/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setCompany(data);
          // Store company context for the session
          sessionStorage.setItem('org_slug', slug);
          sessionStorage.setItem('org_id', data.id);
          sessionStorage.setItem('org_name', data.name);
        } else {
          const err = await res.json();
          setError(err.detail || 'Organization not found');
        }
      } catch (err) {
        setError('Failed to load organization');
      } finally {
        setLoading(false);
      }
    };
    
    if (slug) {
      fetchCompany();
    }
  }, [slug]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await res.json();

      if (res.ok && data.token) {
        // Verify user belongs to this organization
        const userCompanyId = data.user?.company_id;
        if (userCompanyId && userCompanyId !== company.id) {
          setLoginError('Your account is not associated with this organization');
          return;
        }

        // P3 Cookie Migration: cookie was set server-side; nothing to persist.
        navigate('/dashboard');
        window.location.reload();
      } else {
        setLoginError(formatApiDetail(data.detail, 'Invalid credentials'));
      }
    } catch (err) {
      setLoginError('Login failed. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full text-center border border-white/20">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Organization Not Found</h1>
          <p className="text-slate-300 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition"
          >
            Go to Main Login
          </button>
        </div>
      </div>
    );
  }

  const branding = company?.branding || {};
  const primaryColor = branding.primary_color || '#10b981';

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(135deg, #0f172a 0%, #1e293b 50%, ${primaryColor}33 100%)`
      }}
    >
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full border border-white/20">
        {/* Company Header */}
        <div className="text-center mb-8">
          {branding.logo_url ? (
            <img 
              src={branding.logo_url} 
              alt={company.name} 
              className="h-16 mx-auto mb-4"
            />
          ) : (
            <div 
              className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${primaryColor}33` }}
            >
              <Building className="w-8 h-8" style={{ color: primaryColor }} />
            </div>
          )}
          <h1 className="text-2xl font-bold text-white">
            {branding.company_name || company.name}
          </h1>
          {branding.tagline && (
            <p className="text-slate-300 mt-1">{branding.tagline}</p>
          )}
          <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full">
            <Shield className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-slate-300">Powered by HospiceGuard</span>
          </div>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              required
              data-testid="org-email-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              required
              data-testid="org-password-input"
            />
          </div>

          {loginError && (
            <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
              {loginError}
            </div>
          )}

          <button
            type="submit"
            disabled={loginLoading}
            className="w-full py-3 rounded-lg font-medium text-white transition flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: primaryColor }}
            data-testid="org-login-btn"
          >
            {loginLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <LogIn className="w-5 h-5" />
                Sign In to {company.name}
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-slate-400">
            Not a member?{' '}
            <button 
              onClick={() => navigate('/register')}
              className="text-emerald-400 hover:text-emerald-300"
            >
              Request access
            </button>
          </p>
        </div>

        {/* Slug Info */}
        <div className="mt-4 pt-4 border-t border-white/10 text-center">
          <code className="text-xs text-slate-500 bg-white/5 px-2 py-1 rounded">
            /org/{slug}
          </code>
        </div>
      </div>
    </div>
  );
}
